export interface BondingCurveParams {
  supply: number;
  reserveRatio: number; // Between 0 and 1
  basePrice: number;
  volatility: number; // For Black-Scholes
  timeToMaturity: number; // For Black-Scholes, in years
  riskFreeRate: number; // For Black-Scholes
}

// Black-Scholes formula implementation
const calculateBlackScholes = (
  currentPrice: number,
  strikePrice: number,
  timeToMaturity: number,
  riskFreeRate: number,
  volatility: number
) => {
  const d1 = (Math.log(currentPrice / strikePrice) + 
    (riskFreeRate + volatility * volatility / 2) * timeToMaturity) / 
    (volatility * Math.sqrt(timeToMaturity));
  
  const d2 = d1 - volatility * Math.sqrt(timeToMaturity);
  
  // Cumulative normal distribution function
  const cdf = (x: number) => {
    const t = 1 / (1 + 0.2316419 * Math.abs(x));
    const d = 0.3989423 * Math.exp(-x * x / 2);
    const p = d * t * (0.3193815 + t * (-0.3565638 + t * (1.781478 + t * (-1.821256 + t * 1.330274))));
    return x > 0 ? 1 - p : p;
  };

  return currentPrice * cdf(d1) - strikePrice * Math.exp(-riskFreeRate * timeToMaturity) * cdf(d2);
};

export const calculateTokenPrice = ({
  supply,
  reserveRatio,
  basePrice,
  volatility,
  timeToMaturity,
  riskFreeRate
}: BondingCurveParams) => {
  // Logarithmic bonding curve
  const continuousPrice = basePrice * Math.log(1 + supply * reserveRatio);
  
  // Adjust price using Black-Scholes
  const adjustedPrice = calculateBlackScholes(
    continuousPrice,
    basePrice,
    timeToMaturity,
    riskFreeRate,
    volatility
  );

  return adjustedPrice;
};

export const calculateSlippage = (
  currentPrice: number,
  targetPrice: number,
  slippageTolerance: number
) => {
  const priceImpact = Math.abs((targetPrice - currentPrice) / currentPrice) * 100;
  const maxSlippage = slippageTolerance * 100;
  
  return {
    priceImpact,
    exceedsSlippage: priceImpact > maxSlippage,
    maxSlippage
  };
};